import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Loader2, Mail, Send } from 'lucide-react';

const DAYS = [
  ['mon', 'Segunda'], ['tue', 'Terça'], ['wed', 'Quarta'], ['thu', 'Quinta'],
  ['fri', 'Sexta'], ['sat', 'Sábado'], ['sun', 'Domingo'],
];

export default function WeeklySummarySettings({ API, open, onClose }) {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/weekly-summary/settings`, auth()),
        axios.get(`${API}/admin/weekly-summary/preview`, auth()),
      ]);
      setSettings({ ...s.data, recipients: (s.data.recipients || []).join(', ') });
      setPreview(p.data);
    } catch (e) { toast.error('Erro ao carregar configurações'); } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/weekly-summary/settings`, {
        enabled: settings.enabled,
        recipients: settings.recipients,
        day_of_week: settings.day_of_week,
        hour: Number(settings.hour),
        minute: Number(settings.minute),
      }, auth());
      toast.success('Configurações salvas ✅');
    } catch (e) { toast.error('Erro ao salvar'); } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/weekly-summary/send`, {}, auth());
      toast.success(`Resumo enviado para ${(data.recipients || []).join(', ')} ✅`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="weekly-summary-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Resumo Semanal por E-mail</DialogTitle>
        </DialogHeader>
        {loading || !settings ? (
          <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium text-slate-800">Envio automático</div>
                <div className="text-xs text-slate-500">Recebe um resumo do que vence + atrasados</div>
              </div>
              <Switch data-testid="weekly-enabled-switch" checked={!!settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
            </div>

            <div>
              <Label className="text-xs">E-mail(s) — separados por vírgula</Label>
              <Input data-testid="weekly-recipients-input" value={settings.recipients} onChange={(e) => setSettings({ ...settings, recipients: e.target.value })} placeholder="voce@gmail.com, outro@email.com" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-xs">Dia</Label>
                <select data-testid="weekly-day-select" value={settings.day_of_week} onChange={(e) => setSettings({ ...settings, day_of_week: e.target.value })}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  {DAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Hora</Label>
                <Input type="number" min={0} max={23} data-testid="weekly-hour-input" value={settings.hour} onChange={(e) => setSettings({ ...settings, hour: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Min</Label>
                <Input type="number" min={0} max={59} data-testid="weekly-minute-input" value={settings.minute} onChange={(e) => setSettings({ ...settings, minute: e.target.value })} />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Horário de Nova York. O envio automático roda no servidor de produção.</p>

            {preview && (
              <div className="rounded-lg bg-slate-50 border p-3 text-xs text-slate-600" data-testid="weekly-preview">
                Prévia desta semana: <b className="text-red-600">{preview.overdue_count}</b> atrasado(s) · <b className="text-indigo-600">{preview.this_week_count}</b> vencem esta semana.
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={sendNow} disabled={sending || loading} data-testid="weekly-send-now-btn" className="w-full sm:w-auto">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Enviar resumo agora</>}
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="weekly-save-btn" className="w-full sm:w-auto">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
